import { Icon } from 'components';
import { formatLocale } from 'lib';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { formatAmount } from './helpers';

const K = 'billing.timeline';

const STATUS_STYLES = {
    paid: 'bg-emerald-50 text-emerald-700',
    no_charge: 'bg-gray-100 text-gray-600',
    failed: 'bg-red-50 text-red-700',
    refunded: 'bg-amber-50 text-amber-700'
};

const date = (value) => (value ? formatLocale(value, 'MMM d, yyyy') : '');

const money = (rows, empty = '') =>
    rows?.length ? rows.map((r) => formatAmount(r.amount, r.currency)).join(' + ') : empty;

/*
  The billing history of one subscription, the same for a user and an admin: what happened, what it
  cost, and why. Every amount is a charge; a plan change that cost nothing says so. An admin also
  sees fees, split, provider ids, a check against Stripe, and the system events behind it.
*/
const INVOICE_KINDS = ['subscribed', 'plan_change', 'renewal'];

const BillingTimeline = ({ timeline, audience = 'user', onDownloadInvoice }) => {
    const { t } = useTranslation();
    const [openId, setOpenId] = useState(null);
    const [showSystem, setShowSystem] = useState(false);
    const isAdmin = audience === 'admin';
    const { summary, entries = [], technical = [], reconciliation } = timeline || {};
    if (!summary) return null;

    const interval = (value) => (value ? t(`billing.interval_${value}`, { defaultValue: value }) : '');

    const title = (e) => {
        const plan = e.toPlan || '';
        const withInterval = e.interval
            ? (key) => t(`${K}.${key}`, { plan, interval: interval(e.interval) })
            : () => t(`${K}.${e.kind === 'subscribed' ? 'subscribedPlain' : 'planOnly'}`, { plan });

        switch (e.kind) {
            case 'subscribed':
                return withInterval('subscribed');
            case 'renewal':
                return withInterval('renewed');
            case 'plan_change':
                if (e.intervalChanged && e.fromPlan === e.toPlan) {
                    return t(`${K}.switchedInterval`, { plan, interval: interval(e.interval) });
                }
                return withInterval(e.change === 'downgraded' ? 'changedTo' : 'upgradedTo');
            case 'payment':
                return t(`${K}.payment`);
            case 'payment_failed':
                return t(`${K}.paymentFailed`);
            case 'suspended':
                return t(`${K}.suspended`);
            case 'cancelled':
                return t(`${K}.cancelled`);
            case 'refund_requested':
                return t(`${K}.refundRequested`);
            case 'refund_approved':
                return t(`${K}.refundApproved`);
            case 'refund_rejected':
                return t(`${K}.refundRejected`);
            default:
                return e.kind;
        }
    };

    const lineLabel = (line) => {
        if (line.kind === 'credit') return t(`${K}.lineCredit`);
        if (line.kind === 'proration') return t(`${K}.lineProration`);
        return line.description;
    };

    const lineAmount = (line, currency) =>
        line.amount < 0 ? `−${formatAmount(Math.abs(line.amount), currency)}` : formatAmount(line.amount, currency);

    const statusLabel = { paid: 'paid', no_charge: 'noCharge', failed: 'failed', refunded: 'refunded' };

    return (
        <div className="space-y-5">
            {/* Summary */}
            <div className="grid grid-cols-1 gap-3 rounded-lg border border-gray-100 p-4 sm:grid-cols-2">
                <div data-testid="summary-total-paid">
                    <p className="text-xs text-gray-500">{t(`${K}.totalPaid`)}</p>
                    <p className="text-lg font-bold text-gray-900">{money(summary.totalPaid, '—')}</p>
                </div>
                <div data-testid="summary-plan">
                    <p className="text-xs text-gray-500">{t(`${K}.currentPlan`)}</p>
                    <p className="text-sm font-semibold text-gray-900">
                        {t(`${K}.planWithInterval`, { plan: summary.currentPlan?.title, interval: interval(summary.currentPlan?.interval) })}
                    </p>
                </div>
                <div data-testid="summary-next" className="sm:col-span-2">
                    <p className="text-xs text-gray-500">{t(`${K}.nextCharge`)}</p>
                    <p className="text-sm text-gray-900">
                        {summary.nextCharge
                            ? t(`${K}.nextChargeOn`, {
                                  amount: formatAmount(summary.nextCharge.amount, summary.nextCharge.currency),
                                  date: date(summary.nextCharge.date)
                              })
                            : t(`${K}.noFurtherCharge`)}
                    </p>
                </div>
                {summary.refunded?.length > 0 && (
                    <div data-testid="summary-refunded">
                        <p className="text-xs text-gray-500">{t(`${K}.refunded`)}</p>
                        <p className="text-sm font-semibold text-gray-900">{money(summary.refunded)}</p>
                    </div>
                )}
            </div>

            {isAdmin && reconciliation && (
                <div
                    data-testid="reconciliation"
                    className={`rounded-lg px-3 py-2 text-xs ${
                        !reconciliation.available ? 'bg-gray-50 text-gray-600' : reconciliation.matches ? 'bg-emerald-50 text-emerald-800' : 'bg-red-50 text-red-800'
                    }`}
                >
                    {!reconciliation.available && <p>{t(`${K}.reconcileUnavailable`)}</p>}
                    {reconciliation.available && reconciliation.matches && (
                        <p>{t(`${K}.reconcileMatches`, { amount: reconciliation.stripeTotal })}</p>
                    )}
                    {reconciliation.available && !reconciliation.matches && (
                        <>
                            <p>{t(`${K}.reconcileDiffers`, { entries: reconciliation.entriesTotal, stripe: reconciliation.stripeTotal })}</p>
                            {reconciliation.missingInTimeline?.length > 0 && (
                                <p>{t(`${K}.reconcileMissingHere`, { ids: reconciliation.missingInTimeline.join(', ') })}</p>
                            )}
                            {reconciliation.missingInStripe?.length > 0 && (
                                <p>{t(`${K}.reconcileMissingStripe`, { ids: reconciliation.missingInStripe.join(', ') })}</p>
                            )}
                        </>
                    )}
                </div>
            )}

            {/* Entries, newest first */}
            <div className="divide-y divide-gray-100 overflow-hidden rounded-lg border border-gray-100">
                {entries.map((e) => {
                    const open = openId === e.id;
                    const hasDetails = e.lines?.length > 0 || (isAdmin && e.admin);
                    return (
                        <div key={e.id} data-testid={`entry-${e.id}`} className="px-3 py-3">
                            <div className="flex items-start justify-between gap-3">
                                <div className="min-w-0 flex-1">
                                    <p className="text-sm text-gray-900">{title(e)}</p>
                                    <p className="text-xs text-gray-400">
                                        {date(e.at)}
                                        {e.periodStart && e.periodEnd
                                            ? ` · ${t(`${K}.covers`, { from: date(e.periodStart), to: date(e.periodEnd) })}`
                                            : ''}
                                    </p>
                                </div>
                                <div className="flex shrink-0 flex-col items-end gap-1">
                                    {e.status === 'no_charge' ? (
                                        <span className="text-sm text-gray-500">{t(`${K}.status.noCharge`)}</span>
                                    ) : e.charged != null ? (
                                        <span className="text-sm font-bold text-gray-900">{formatAmount(e.charged, e.currency)}</span>
                                    ) : null}
                                    {onDownloadInvoice && INVOICE_KINDS.includes(e.kind) && e.status === 'paid' && (
                                        <button
                                            type="button"
                                            onClick={() => onDownloadInvoice(e.id)}
                                            aria-label={t('billing.downloadInvoice')}
                                            title={t('billing.downloadInvoice')}
                                            className="rounded-md p-1 text-gray-500 hover:bg-gray-100 hover:text-gray-900"
                                        >
                                            <Icon type="download" dimensions={16} col="currentColor" />
                                        </button>
                                    )}
                                    {statusLabel[e.status] && e.status !== 'no_charge' && (
                                        <span className={`rounded px-1.5 py-0.5 text-[11px] font-semibold ${STATUS_STYLES[e.status]}`}>
                                            {t(`${K}.status.${statusLabel[e.status]}`)}
                                        </span>
                                    )}
                                </div>
                            </div>

                            {hasDetails && (
                                <button
                                    type="button"
                                    onClick={() => setOpenId(open ? null : e.id)}
                                    aria-expanded={open}
                                    className="mt-1.5 text-xs font-semibold text-gray-500 hover:text-gray-900"
                                >
                                    {open ? t(`${K}.hideDetails`) : t(`${K}.details`)}
                                </button>
                            )}

                            {open && (
                                <div className="mt-2 space-y-1 rounded-md bg-gray-50 px-3 py-2 text-xs text-gray-700">
                                    {e.lines?.map((line, i) => (
                                        <div key={i} className="flex justify-between gap-3">
                                            <span>{lineLabel(line)}</span>
                                            <span className="font-medium">{lineAmount(line, e.currency)}</span>
                                        </div>
                                    ))}
                                    {isAdmin && e.admin && (
                                        <div className="mt-2 space-y-1 border-t border-gray-200 pt-2 text-gray-600">
                                            {e.admin.providerInvoiceId && (
                                                <div className="flex justify-between gap-3">
                                                    <span>{t(`${K}.admin.invoice`)}</span>
                                                    <span className="font-mono">{e.admin.providerInvoiceId}</span>
                                                </div>
                                            )}
                                            {e.admin.stripeFee != null && (
                                                <div className="flex justify-between gap-3">
                                                    <span>{t(`${K}.admin.stripeFee`)}</span>
                                                    <span>{formatAmount(e.admin.stripeFee, e.currency)}</span>
                                                </div>
                                            )}
                                            {e.admin.applicationFeeAmount != null && (
                                                <div className="flex justify-between gap-3">
                                                    <span>{t(`${K}.admin.platformShare`)}</span>
                                                    <span>{formatAmount(e.admin.applicationFeeAmount, e.currency)}</span>
                                                </div>
                                            )}
                                            {e.admin.manualTransferStatus && (
                                                <div className="flex justify-between gap-3">
                                                    <span>{t(`${K}.admin.teamTransfer`)}</span>
                                                    <span>
                                                        {e.admin.manualTransferStatus}
                                                        {e.admin.manualTransferDueAmount != null
                                                            ? ` · ${formatAmount(e.admin.manualTransferDueAmount, e.currency)}`
                                                            : ''}
                                                    </span>
                                                </div>
                                            )}
                                            {e.admin.providerEventIds?.map((id) => (
                                                <div key={id} className="flex justify-between gap-3">
                                                    <span>{t(`${K}.admin.providerEvent`)}</span>
                                                    <span className="font-mono">{id}</span>
                                                </div>
                                            ))}
                                            {e.admin.isVerified === false && <p>{t(`${K}.admin.unverified`)}</p>}
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>

            {isAdmin && technical.length > 0 && (
                <div>
                    <button
                        type="button"
                        onClick={() => setShowSystem(!showSystem)}
                        aria-expanded={showSystem}
                        className="text-xs font-semibold text-gray-500 hover:text-gray-900"
                    >
                        {t(`${K}.systemEvents`, { count: technical.length })}
                    </button>
                    {showSystem && (
                        <ul className="mt-2 space-y-1 text-xs text-gray-600">
                            {technical.map((e) => (
                                <li key={e.id} className="flex justify-between gap-3">
                                    <span className="font-mono">{e.eventType}</span>
                                    <span>{date(e.at)}</span>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            )}
        </div>
    );
};

export default BillingTimeline;
